import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, Field, Input, Modal } from '@icebreaker/ui';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { checkOwnerEmail, ownerEmailCheckReset } from '../store/organizations.slice';
import { looksLikeEmail } from './dialog-parts';
import styles from './organizations.module.css';

/**
 * İŞ ADRESİ UYARISI (KH8 · Story H7 · TP1).
 *
 * "Kurum aç" ve "Sahip davetini başka adrese gönder" diyaloglarının sahip
 * e-postası alanı. Kontrol alan odaktan çıkınca başlar (`POST
 * /panel/organizations/owner-email-check`, personel ağacı). Adres bir kişisel
 * hesaptaysa alan bekleme rengine geçer, gönder AÇIK kalır ve basılınca ikinci
 * onay gelir: karar personeldedir.
 *
 * Kontrol düşerse gönderim kilitlenmez (fail-open): sunucu gönderim anında
 * aynı kuralı yeniden koşar ve kararı denetim defterine yazar.
 */
export type OwnerEmailStatus = 'idle' | 'checking' | 'personal' | 'clear' | 'unknown';

export function normalizeOwnerEmail(value: string): string {
  return value.trim().toLowerCase();
}

export function useOwnerEmailCheck(email: string): {
  readonly status: OwnerEmailStatus;
  readonly check: () => void;
} {
  const dispatch = useAppDispatch();
  const state = useAppSelector((root: RootState) => root.panelOrgs.ownerEmailCheck);
  const normalized = normalizeOwnerEmail(email);

  useEffect(() => {
    dispatch(ownerEmailCheckReset());
    return () => {
      dispatch(ownerEmailCheckReset());
    };
  }, [dispatch]);

  // Sonuç yalnız SORULAN adrese aittir: alan değişince uyarı düşer, odaktan çıkınca yeniden sorulur.
  const status: OwnerEmailStatus =
    state.email !== normalized || state.phase === 'idle'
      ? 'idle'
      : state.phase === 'loading'
        ? 'checking'
        : state.phase === 'failed'
          ? 'unknown'
          : state.personal
            ? 'personal'
            : 'clear';

  const check = useCallback(() => {
    if (!looksLikeEmail(normalized)) return;
    if (state.email === normalized && state.phase !== 'idle' && state.phase !== 'failed') return;
    dispatch(checkOwnerEmail.request({ ownerEmail: normalized }));
  }, [dispatch, normalized, state.email, state.phase]);

  return { status, check };
}

/**
 * GÖNDERİM KAPISI: biçimi geçerli adresin kontrolü yoksa önce sorulur, sonuç
 * gelince gönderim kendiliğinden sürer (Enter ile alan odaktan çıkmadan
 * gönderilebilir, kontrol sürerken basılan düğme de bekler). Bu arada alan değişirse bekleyen gönderim düşer.
 * Kişisel hesapta ikinci onay açılır.
 */
export function useOwnerEmailGate({
  email,
  send,
  inputId,
}: {
  email: string;
  send: () => void;
  /** "Adresi değiştir" sonrası odaklanıp seçilecek alan (kit `Input` ref taşımaz). */
  inputId: string;
}): {
  readonly status: OwnerEmailStatus;
  readonly check: () => void;
  readonly confirming: boolean;
  readonly request: () => void;
  readonly sendAnyway: () => void;
  readonly changeAddress: () => void;
} {
  const { status, check } = useOwnerEmailCheck(email);
  const normalized = normalizeOwnerEmail(email);
  const [pendingFor, setPendingFor] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [refocus, setRefocus] = useState(false);

  useEffect(() => {
    if (pendingFor === null || status === 'checking') return;
    if (pendingFor !== normalized) {
      setPendingFor(null);
      return;
    }
    if (status === 'idle') return;
    setPendingFor(null);
    if (status === 'personal') setConfirming(true);
    else send();
  }, [normalized, pendingFor, send, status]);

  // "Adresi değiştir": form geri gelir, alan SEÇİLİ hâlde odakta.
  useEffect(() => {
    if (confirming || !refocus) return;
    setRefocus(false);
    const input = document.getElementById(inputId);
    if (input instanceof HTMLInputElement) {
      input.focus();
      input.select();
    }
  }, [confirming, inputId, refocus]);

  // Kontrol sürerken basış KAYBOLMAZ (sıra 102): alandan çıkış kontrolü
  // başlatır, aynı tıklama sonuç gelince gönderimi sürdürür.
  const request = () => {
    if (status === 'checking') setPendingFor(normalized);
    else if (status === 'personal') setConfirming(true);
    else if (status === 'idle') {
      setPendingFor(normalized);
      check();
    } else send();
  };

  return {
    status,
    check,
    confirming,
    request,
    sendAnyway: () => {
      setConfirming(false);
      send();
    },
    changeAddress: () => {
      setConfirming(false);
      setRefocus(true);
    },
  };
}

/**
 * Alan + kontrol hâli + uyarı bloğu (TP1 "UYARILI" ve "KONTROL EDİLİYOR" kareleri).
 * Kontrol düşerse sebep cümlesi ve "Tekrar kontrol et" çizilir.
 */
export function OwnerEmailField({
  id,
  value,
  status,
  error,
  disabled,
  placeholder,
  onChange,
  onBlur,
  onRetry,
}: {
  id: string;
  value: string;
  status: OwnerEmailStatus;
  error: string | undefined;
  disabled: boolean;
  placeholder: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  onRetry: () => void;
}) {
  const t = useTranslation();
  const warn = status === 'personal' && !error;
  const checking = status === 'checking' && !error;
  // Kontrol düştü: gönderim yine açık (fail-open), ama personel bunu bilir ve yeniden sorabilir.
  const unknown = status === 'unknown' && !error;
  const statusId = `${id}-check`;
  return (
    <Field id={id} label={t.t('admin.orgs.createDialog.ownerEmail')} error={error}>
      <span className={styles.adornedInput}>
        <Input
          id={id}
          type="email"
          className={[styles.mono, styles.adornedField, warn ? styles.inputWarn : '']
            .filter(Boolean)
            .join(' ')}
          autoComplete="off"
          value={value}
          disabled={disabled}
          invalid={Boolean(error)}
          aria-describedby={warn || checking || unknown ? statusId : undefined}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
        />
        {checking ? <span className={styles.checkSpinner} aria-hidden="true" /> : null}
        {warn ? <span className={styles.warnMark} aria-hidden="true" /> : null}
      </span>
      {checking ? (
        <span id={statusId} role="status" className={styles.checkStatus}>
          {t.t('admin.orgs.ownerCheck.checking')}
        </span>
      ) : null}
      {warn ? (
        <span id={statusId} role="status" className={styles.checkWarn}>
          {t.t('admin.orgs.ownerCheck.warn')}
        </span>
      ) : null}
      {unknown ? (
        <span className={styles.checkFailed}>
          <span id={statusId} role="status">
            {t.t('admin.orgs.ownerCheck.unavailable')}
          </span>
          <button
            type="button"
            className={styles.checkRetry}
            disabled={disabled}
            onClick={() => {
              // Düğme "kontrol ediliyor" hâlinde kalkar; odak gövdeye düşmesin, alana döner.
              const input = document.getElementById(id);
              if (input instanceof HTMLInputElement) input.focus();
              onRetry();
            }}
          >
            {t.t('admin.orgs.ownerCheck.retry')}
          </button>
        </span>
      ) : null}
    </Field>
  );
}

/**
 * İKİNCİ ONAY (TP1 "İKİNCİ ONAY" karesi). Dolu düğme güvenli yoldur ("Adresi
 * değiştir"): Esc ve perde de onu seçer, "Yine de gönder" ancak açık basışla
 * gider. Form katmanının YERİNE çizilir; girilen bilgiler diyalogda durur.
 */
export function OwnerEmailConfirm({
  email,
  staffName,
  onSendAnyway,
  onChange,
}: {
  email: string;
  staffName: string;
  onSendAnyway: () => void;
  onChange: () => void;
}) {
  const t = useTranslation();
  const title = t.t('admin.orgs.ownerCheck.confirmTitle');
  return (
    <Modal
      open
      title={title}
      closeLabel={t.t('admin.orgs.ownerCheck.change')}
      onClose={onChange}
      className={styles.confirmPanel}
      // Kapatma çarpısı ve başlık şeridi YOK (TP1): işaret, başlık ve gövde tek blokta.
      header={<></>}
      footer={
        <div className={[styles.footerActions, styles.confirmActions].join(' ')}>
          <Button variant="secondary" onClick={onSendAnyway}>
            {t.t('admin.orgs.ownerCheck.sendAnyway')}
          </Button>
          <Button variant="primary" autoFocus onClick={onChange}>
            {t.t('admin.orgs.ownerCheck.change')}
          </Button>
        </div>
      }
    >
      <ConfirmHead title={title}>
        <span className={styles.confirmMail}>{email}</span>{' '}
        {t.t('admin.orgs.ownerCheck.confirmBody')}
      </ConfirmHead>
      <p className={styles.confirmAudit}>
        {t.t('admin.orgs.ownerCheck.confirmAudit', { name: staffName })}
      </p>
    </Modal>
  );
}

function ConfirmHead({ title, children }: { title: string; children: ReactNode }) {
  return (
    <span className={styles.confirmHead}>
      <span className={styles.confirmMark} aria-hidden="true" />
      <span className={styles.confirmText}>
        <span className={styles.confirmTitle}>{title}</span>
        <span className={styles.confirmBody}>{children}</span>
      </span>
    </span>
  );
}
